import { PedagoMessagePopupVariantProps, PedagoPopup, ReactIcons } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MeetingSessionStatus } from "@kalyzee/kast-web-sdk/http";
import { MeetingCallState, MeetingUserDataCallStatus } from "@kalyzee/kast-web-sdk/websocket";
import { useState } from "react";
import { updateUserData } from "../../../helpers/meeting";
import { pedagogyExtraUserDataFromString } from "../../../helpers/pedagogy";
import { addPopup, removePopup, removePopupByTag } from "../../../helpers/popup";
import { toastError } from "../../../helpers/toast";
import { useMeetingSelector } from "../../../hooks/meeting";
import { PageMasterMode, usePageContextRef } from "../../navigation/PageContext";

export interface PedagogyPopupProps {
  originContainer?: MeetingContainerAPI;
  pedagogyContainer?: MeetingContainerAPI;
  pedagogyIsHere?: boolean;
}

export const PedagogyPopup = ({ originContainer, pedagogyContainer, pedagogyIsHere }: PedagogyPopupProps) => {
  const pageRef = usePageContextRef();

  const [meetingId] = useMeetingSelector(originContainer, (state) => state.meeting?.id);
  const [userData] = useMeetingSelector(originContainer, (state) => {
    const userId = state.session?.user?.id;
    if (!userId) return undefined;
    return state.meeting?.usersData?.find((u) => u.userId === userId);
  });
  const [callWasDone] = useMeetingSelector(
    originContainer, 
    (state) => state.meeting?.callEnabled
      && (!state.meeting?.callState || state.meeting.callState === MeetingCallState.CLOSED)
      && (state.meeting?.callWasDone || state.meeting?.endCallWasDone)
  );

  const [messageDraft, setMessageDraft] = useState<string>();
  const [waitingSince] = useState(new Date());

  const call = userData?.call;
  const canEnter = callWasDone 
    ? (call?.status === MeetingUserDataCallStatus.ONLINE || call?.status === MeetingUserDataCallStatus.ONSITE) 
    : call?.status !== MeetingUserDataCallStatus.KICKED;

  if (!meetingId) return null;

  //////////////////// HANDLERS ////////////////////

  const quit = (leavePage = true) => {
    pedagogyContainer?.leave();
    originContainer?.leave();
    if (leavePage) pageRef.current?.goTo?.(PageMasterMode.LIST, { id: meetingId });
  };

  const updateCurrentUserData = async () => {
    if (!userData) return undefined;
    if (!call) return undefined;
    const extraData = pedagogyExtraUserDataFromString(call.extraData);
    return await updateUserData(
      meetingId,
      {
        mode: "by_user_data_id",
        id: userData.id,
      },
      {
        call: {
          seenByUser: true,
          // if the status is still unverified, we do not reset our message
          userMessage: call.status === MeetingUserDataCallStatus.UNVERIFIED || extraData.notify ? undefined : "",
        },
      }
    );
  };

  const onValidate = async () => {
    if (!call) return;
    const sessionId = originContainer?.store.getState().session?.id;
    if (!sessionId) return;
    const response = await updateCurrentUserData();
    if (!response) return;
    const extraData = pedagogyExtraUserDataFromString(call.extraData);
    if (extraData.notify) return;
    if (canEnter) {
      // Setting session back to running,
      // TODO : careful, if updateUserData works and this fails ... we will be stuck
      // in pause mode
      const result = await originContainer.updater.update(sessionId, { status: MeetingSessionStatus.RUNNING });
      if (!result || result?.error) {
        toastError("Une erreur est apparue");
        quit();
      } else {
        pedagogyContainer?.leave();
        pageRef.current?.goTo?.(PageMasterMode.MEETING_DASHBOARD, { id: meetingId });
      }
    } else if (call.status !== MeetingUserDataCallStatus.UNVERIFIED) {
      quit();
    }
  };

  const onSendMessage = () => {
    if (!call || !userData) return;
    if (!messageDraft) {
      toastError("Veuillez précisez la raison de votre retard en laissant un message.");
      return;
    }
    updateUserData(
      meetingId,
      {
        mode: "by_user_data_id",
        id: userData.id,
      },
      {
        call: {
          updatedAtVersionGuard: call?.updatedAt ?? "none",
          userMessage: messageDraft,
        },
      }
    );
  };

  ///////////////// RENDERING ////////////////////

  if (
    call &&
    !call.seenByUser &&
    (call.status !== MeetingUserDataCallStatus.UNVERIFIED ||
      // perhaps call is not done but there is something to see
      (call.status === MeetingUserDataCallStatus.UNVERIFIED && call.message))
  ) {
    // There is something to see

    const extraData = pedagogyExtraUserDataFromString(call.extraData);
    const late = extraData.late;
    const report = extraData.report;

    let title = "";
    let description = "";
    let mainMessage = "";
    let secondaryMessage = "";

    let actionType: "kick" | "let_in" | "notify" = "let_in";
    let severity: PedagoMessagePopupVariantProps["severity"] = "info";
    let Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> = ReactIcons.HandOutline;

    let kicked = false;
    if ((call.status === MeetingUserDataCallStatus.UNVERIFIED || extraData.notify) && call.message) {
      // User was not treated yet, but is receiving a message still
      Icon = ReactIcons.ChatOutline;
      title = "Message de la pédagogie";
      mainMessage = call.message ?? "";
      actionType = "notify";
    } else if (late) {
      // User was treated and is late
      title = "Vous avez signalé votre présence";
      description = "L'équipe pédagogique va étudier votre situation d’ici peu: veuillez rester en ligne et ne pas quitter cet écran.";
      if (report) {
        if (canEnter) {
          mainMessage = "Accès au cours autorisé avec avertissement";
          secondaryMessage = "Ce retard sera inscrit à votre dossier apprenant";
          severity = "warning";
        } else {
          mainMessage = "Vous n'êtes pas autorisé à rejoindre le cours";
          secondaryMessage = "Votre absence sera inscrit à votre dossier apprenant.";
          actionType = "kick";
          severity = "error";
          kicked = true;
        }
      } else {
        mainMessage = "Vous êtes autorisé à rejoindre le cours";
        secondaryMessage = "Nous vous invitons à respecter les horaires de début de séance";
        severity = "success";
      }
    } else {
      // User was treated in the past and this is a spontaneous message
      actionType = "notify";
      Icon = ReactIcons.WarningTriangle;
      if (report) {
        mainMessage = "Avertissement";
        secondaryMessage = "Cet avertissement sera inscrit à votre dossier apprenant.";
        if (canEnter) {
          title = "Avertissement";
          severity = "warning";
        } else {
          title = "Exclusion du cours";
          actionType = "kick";
          severity = "error";
          kicked = true;
        }
      } else {
        Icon = ReactIcons.ChatOutline;
        title = "Message de la pédagogie";
        mainMessage = call.message ?? "";
      }
    }

    if (!mainMessage) return null;

    const render = (validate: () => void) => (
      <PedagoPopup.Message
        title={title}
        description={description}
        mainMessage={mainMessage}
        secondaryMessage={secondaryMessage}
        severity={severity}
        details={
          call.message && (late || report)
            ? {
                title: late ? "Motif : retard" : "Motif : comportement inapproprié",
                contentOpening: "",
                content: call.message,
              }
            : undefined
        }
        button={{
          type: actionType === "let_in" ? "blue" : "white",
          text:
            actionType === "let_in"
              ? "Rejoindre le cours"
              : actionType === "kick"
              ? "OK"
              : call.status === MeetingUserDataCallStatus.UNVERIFIED
              ? "Valider"
              : "Reprendre le cours",
          onClick: validate,
        }}
        icon={<Icon style={{ width: "100%", height: "100%" }} />}
        containerStyle={{ zIndex: 11000 }}
      />
    );

    if (kicked) {
      // if is kicked, update & quit immediatly
      updateCurrentUserData();
      quit();
      const element = render(() => removePopup(element));
      removePopupByTag("pedagogy_popup");
      addPopup(element, "pedagogy_popup");
      return null;
    }

    return render(onValidate);
  } else if (!canEnter && !pedagogyIsHere) {
    // There is no message to see and i can't enter
    // AND pedagogy expert is not here, otherwise this popup is not shown
    if (call?.userMessage) {
      return (
        <PedagoPopup.Wait
          title="Vous avez signalé votre présence"
          description="L'équipe pédagogique va étudier votre situation d’ici peu : veuillez rester en ligne et ne pas quitter cet écran."
          waitingSince={waitingSince}
          containerStyle={{ zIndex: 11000 }}
          // refresh={{
          //   cooldown: 60,
          //   onRefresh: () => {},
          //   text: "Vous pourrez notifier à nouveau la pédagogie de votre présence dans {time}",
          //   buttonText: "Rappeler la pédagogie",
          // }}
        />
      );
    }
    return (
      <PedagoPopup.Submit
        title={call?.status === MeetingUserDataCallStatus.KICKED ? "Vous avez été exclu" : "Vous êtes en retard"}
        description={
          call?.status === MeetingUserDataCallStatus.KICKED
            ? "Vous ne pouvez pas rejoindre le cours."
            : "Le cours a démarré et l’appel a été fait. Vous êtes actuellement en salle d’attente."
        }
        submitTitle={call?.status === MeetingUserDataCallStatus.KICKED ? "Écrivez un message à la pédagogie" : "Précisez la raison de votre retard :"}
        submitButtonText="Signaler mon arrivée"
        value={messageDraft}
        onChange={setMessageDraft}
        onOver={onSendMessage}
        containerStyle={{ zIndex: 11000 }}
      />
    );
  }

  // There is no message to see and i can enter
  return null;
};
